import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';
import InlineNotification from '../components/InlineNotification/InlineNotification';
import '../components/InlineNotification/InlineNotification.scss';  

type NotificationType = 'warning' | 'error' | 'success' | 'info';

interface InlineNotification {
  id: number;
  type: NotificationType;
  title: string;
  description?: string;
  failedTryDescription?: string;
  onTryAgain?: () => Promise<void>;
  attempts?: number;

}

interface InlineNotificationContextType {
  addInlineNotification: (type: NotificationType, title: string, description?: string, failedTryDescription?: string, onTryAgain?: () => Promise<void>, attempts?: number) => void;
  removeInlineNotification: () => void;
}

const InlineNotificationContext = createContext<InlineNotificationContextType | undefined>(undefined);

export const useInlineNotification = () => {
  const context = useContext(InlineNotificationContext);
  if (!context) {
    throw new Error('useInlineNotification must be used within an InlineNotificationProvider');
  }
  return context;
};

export const InlineNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentNotification, setCurrentNotification] = useState<InlineNotification | null>(null);

  const addInlineNotification = useCallback((type: NotificationType, title: string, description?: string, failedTryDescription?: string, onTryAgain?: () => Promise<void>, attempts?: number) => {
    const id = Date.now();
    setCurrentNotification({ id, type, title, description, failedTryDescription, onTryAgain, attempts });
  }, []);

  const removeInlineNotification = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  const contextValue = useMemo(() => ({
    addInlineNotification,
    removeInlineNotification
  }), [addInlineNotification, removeInlineNotification]);

  return (
    <InlineNotificationContext.Provider value={contextValue}>
      {children}
      {currentNotification && (
        <div className="inline-notification-container">
          <InlineNotification
            key={currentNotification.id}
            type={currentNotification.type}
            title={currentNotification.title}
            description={currentNotification.description}
            failedTryDescription={currentNotification.failedTryDescription}
            onTryAgain={currentNotification.onTryAgain}
            attempts = {currentNotification.attempts}
          />
        </div>
      )}
    </InlineNotificationContext.Provider>
  );
};